import type { Area } from "./permissions.js";

/**
 * KI-Assistent (MCP): Werkzeuge, die Claude & Co. beim Kunden nutzen dürfen. Sichtbar ist ein Werkzeug nur, wenn
 * (1) das zugehörige Modul gebucht ist, (2) die verbundene Person den Bereich sehen darf und (3) Logiful es nicht
 * gesperrt hat (global oder je Kunde). Gesperrte Werkzeuge erscheinen gar nicht – der Assistent weiß nichts von ihnen.
 */
export interface McpToolMeta {
  name: string;
  /** Anzeige im Portal (Admin-Schalter) */
  label: string;
  /** Bereich für die Benutzerrechte (null = für alle mit Zugang) */
  area: Area | null;
  /** nötiges Modul/Funktion */
  module?: [moduleKey: string, feature: string];
  /** standardmäßig gesperrt, bis Logiful es freigibt */
  sensitive: boolean;
}

export const MCP_TOOLS: McpToolMeta[] = [
  { name: "search_orders", label: "Aufträge suchen", area: "orders", sensitive: false },
  { name: "get_order", label: "Auftrag im Detail (Status, Gründe, Sendungen)", area: "orders", sensitive: false },
  { name: "get_stock", label: "Bestände", area: "stock", sensitive: false },
  { name: "get_replenishment", label: "Reichweite & Nachbestellen", area: "stock", sensitive: false },
  { name: "list_inbounds", label: "Wareneingänge", area: "inbounds", sensitive: false },
  { name: "list_returns", label: "Retouren", area: "returns", sensitive: false },
  { name: "get_action_items", label: "Handlungsbedarf", area: null, sensitive: false },
  { name: "get_performance", label: "Leistungskennzahlen", area: "reports", module: ["analytics", "performance"], sensitive: true },
  { name: "get_costs", label: "Kostenübersicht", area: "costs", module: ["analytics", "costs"], sensitive: true },
  { name: "list_b2b_deliveries", label: "B2B-Lieferungen", area: "orders", module: ["b2b", "b2b_orders"], sensitive: false },
  { name: "list_fba_shipments", label: "Pre-FBA-Sendungen", area: "orders", module: ["fba", "prefba"], sensitive: false },
  { name: "get_delivery_overview", label: "Track & Trace: Zustellungen, Laufzeiten, Probleme", area: "orders", module: ["tracking", "delivery_status"], sensitive: false },
];

/** kein Werkzeug, sondern ein Schalter: Namen, Straße, E-Mail, Telefon der Endkunden mit ausgeben */
export const MCP_END_CUSTOMER_DATA = "end_customer_data";
export const MCP_SWITCHES: { name: string; label: string; sensitive: boolean }[] = [
  ...MCP_TOOLS.map((t) => ({ name: t.name, label: t.label, sensitive: t.sensitive })),
  { name: MCP_END_CUSTOMER_DATA, label: "Endkundendaten (Name, Straße, E-Mail, Telefon)", sensitive: true },
];

/** Logiful-Schalter: global und je Kunde (Kunde überschreibt global, sonst Standard je Werkzeug) */
export interface McpToolSwitches {
  global: Record<string, boolean>;
  tenants: Record<string, Record<string, boolean>>;
}

export function mcpSwitchOn(s: McpToolSwitches, tenantId: string, name: string): boolean {
  const t = s.tenants[tenantId]?.[name];
  if (t !== undefined) return t;
  const g = s.global[name];
  if (g !== undefined) return g;
  return !MCP_SWITCHES.find((x) => x.name === name)?.sensitive;
}

/** Welche Werkzeuge darf diese Verbindung nutzen? */
export function mcpAllowedTools(ctx: {
  tenantId: string;
  switches: McpToolSwitches;
  features: Partial<Record<string, string[]>>;
  canView: (area: Area) => boolean;
}): McpToolMeta[] {
  return MCP_TOOLS.filter(
    (t) =>
      (!t.module || !!ctx.features[t.module[0]]?.includes(t.module[1])) &&
      (!t.area || ctx.canView(t.area)) &&
      mcpSwitchOn(ctx.switches, ctx.tenantId, t.name),
  );
}
